/**
 * Screen-level phases: the four §16.3 rows that are not per-role (AC-026).
 *
 * Loading, Empty, Partial outage and Error are properties of a *screen*, not of
 * a role: they say whether the data behind the screen can be shown at all. They
 * are modelled separately from `AgentDisplayState` so that a screen cannot
 * accidentally render a role list while the fetch that produced it failed.
 *
 * The important rule, and the reason this is a total function over an explicit
 * union rather than a chain of `if (data)` checks: there is no branch in which a
 * missing or stale result falls through to the healthy rendering.
 */

import type { OfficeScene } from '@autonomous-team/ui-projections';

import { formatAgeMs } from './format.js';

/** Result of any Console fetch. `error` carries the *source* so §16.3's "source-specific error" is possible. */
export type LoadState<T> =
  | { readonly status: 'loading'; readonly source: string }
  | { readonly status: 'ready'; readonly value: T; readonly receivedAt: number }
  | { readonly status: 'error'; readonly source: string; readonly message: string; readonly retryable: boolean };

export function loading<T>(source: string): LoadState<T> {
  return { status: 'loading', source };
}

export function ready<T>(value: T, receivedAt: number): LoadState<T> {
  return { status: 'ready', value, receivedAt };
}

export function failed<T>(source: string, message: string, retryable = true): LoadState<T> {
  return { status: 'error', source, message, retryable };
}

/**
 * The §16.3 row a screen is currently in.
 *
 * `partial-outage` is separate from `ready` on purpose: the data is renderable,
 * but it must be rendered frozen and labelled, never as a healthy view.
 */
export type ViewPhase = 'loading' | 'empty' | 'ready' | 'partial-outage' | 'error';

export interface PhaseDescription {
  readonly phase: ViewPhase;
  /** Heading text; always present so the phase is never conveyed by layout alone. */
  readonly title: string;
  readonly detail: string;
  /** Text for the `aria-live` region, or `''` when there is nothing worth announcing. */
  readonly announcement: string;
  /** Whether the screen may render its normal content underneath. */
  readonly rendersContent: boolean;
}

/**
 * Classifies a scene load into a §16.3 row.
 *
 * Order matters and is the safety property, exactly as in `deriveAgentState`:
 * error, then loading, then staleness, then emptiness, and only then healthy.
 */
export function scenePhase(state: LoadState<OfficeScene>): PhaseDescription {
  if (state.status === 'error') {
    return {
      phase: 'error',
      title: `Cannot read ${state.source}`,
      detail: state.message,
      announcement: `Error reading ${state.source}. ${state.message}`,
      rendersContent: false,
    };
  }

  if (state.status === 'loading') {
    return {
      phase: 'loading',
      title: `Reading ${state.source}…`,
      detail: 'No state is shown until the source answers.',
      announcement: '',
      rendersContent: false,
    };
  }

  const scene = state.value;

  // AC-026: staleness is decided before emptiness and before health. A frozen
  // scene with no agents is a partial outage, not an empty office.
  if (scene.mode === 'frozen') {
    const board = scene.sourceFreshness.board;
    const execution = scene.sourceFreshness.execution;
    const stalest = (board.ageMs ?? Number.POSITIVE_INFINITY) > (execution.ageMs ?? Number.POSITIVE_INFINITY)
      ? { name: 'Board', value: board }
      : { name: 'Execution engine', value: execution };
    return {
      phase: 'partial-outage',
      title: 'UNKNOWN — the scene is frozen',
      detail:
        `${stalest.name} data was last observed ${formatAgeMs(stalest.value.ageMs)}. ` +
        'The last known state is still shown, frozen and labelled; it is not being presented as current.',
      announcement: 'A data source is stale. The scene is frozen and states are shown as unknown.',
      rendersContent: true,
    };
  }

  if (scene.agents.length === 0 && scene.tasks.length === 0) {
    return {
      phase: 'empty',
      title: 'No roles configured',
      detail: 'Run the setup wizard to connect a board, a provider and a sample team.',
      announcement: '',
      rendersContent: false,
    };
  }

  return {
    phase: 'ready',
    title: 'Live',
    detail: `Projection generated ${formatAgeMs(Math.max(0, state.receivedAt - Date.parse(scene.generatedAt)))}.`,
    announcement: '',
    rendersContent: true,
  };
}

export interface EmptyCopy {
  readonly title: string;
  readonly detail: string;
}

/**
 * Generic phase for any loaded value.
 *
 * `isEmpty` is a predicate rather than a length check so that "empty" can mean
 * whatever emptiness means for the value — no rows, no roles, no pending
 * decisions — without every caller re-implementing the loading and error arms
 * and getting the ordering subtly wrong.
 */
export function valuePhase<T>(
  state: LoadState<T>,
  isEmpty: (value: T) => boolean = () => false,
  empty: EmptyCopy = { title: 'Nothing to show', detail: '' },
): PhaseDescription {
  if (state.status === 'error') {
    return {
      phase: 'error',
      title: `Cannot read ${state.source}`,
      detail: state.message,
      announcement: `Error reading ${state.source}. ${state.message}`,
      rendersContent: false,
    };
  }
  if (state.status === 'loading') {
    return {
      phase: 'loading',
      title: `Reading ${state.source}…`,
      detail: 'No rows are shown until the source answers.',
      announcement: '',
      rendersContent: false,
    };
  }
  if (isEmpty(state.value)) {
    return { phase: 'empty', title: empty.title, detail: empty.detail, announcement: '', rendersContent: false };
  }
  return { phase: 'ready', title: 'Live', detail: '', announcement: '', rendersContent: true };
}

/** List phase for the screens that are not scene-driven (runs, skills, costs, audit). */
export function listPhase<T>(state: LoadState<readonly T[]>, emptyTitle: string, emptyDetail: string): PhaseDescription {
  return valuePhase(state, (value) => value.length === 0, { title: emptyTitle, detail: emptyDetail });
}

/**
 * Divergences worth shouting about (§16.4, AC-032).
 *
 * Returned as structured data rather than a string so the banner, the office
 * overlay and the Truth View can all present the same set without re-deriving
 * it — and so the count cannot disagree between them.
 */
export interface DivergenceSummary {
  readonly taskBindingId: string;
  readonly title: string;
  readonly boardState: string;
  readonly boardFreshness: string;
  readonly executionState: string;
  readonly executionFreshness: string;
  readonly explanation: string;
}

export function divergences(scene: OfficeScene): readonly DivergenceSummary[] {
  return scene.tasks
    .filter((task) => task.divergence.agreement === 'diverged')
    .map((task) => ({
      taskBindingId: task.taskBindingId,
      title: task.title,
      boardState: task.divergence.boardState.value ?? 'unknown',
      boardFreshness: task.divergence.boardState.freshness,
      executionState: task.divergence.executionState.value ?? 'unknown',
      executionFreshness: task.divergence.executionState.freshness,
      explanation: task.divergence.explanation,
    }));
}
